import { supabase } from '../supabaseClient';
import {
  sendEmail,
  briefQuestionAskedEmail,
  briefQuestionAnsweredEmail,
  briefQuestionPublishedEmail,
  briefQuestionClosedEmail,
  briefUpdateRequestEmail,
  briefStatusUpdateEmail,
} from './email';
import { fetchActivePortalUsersBySupplier } from './briefRecipients';

// Notification fan-outs for the supplier-questions feature (§7). Every send is
// best-effort per-recipient (sendEmail throws on failure); a failed email never
// blocks the DB write that already happened. All supplier recipients are ACTIVE
// portal users (supplier_users.is_active) — the existing gotcha — so each helper
// reports suppliers with no deliverable address for the admin UI to surface.

const appUrl = () => (typeof window !== 'undefined' ? window.location.origin : '');

const logSupplierEmail = async (supplierId, emailKind, recipients, sentBy) => {
  if (!recipients.length) return;
  try {
    await supabase.from('supplier_email_log').insert([{
      supplier_id: supplierId, email_kind: emailKind, recipients, sent_by: sentBy || null,
    }]);
  } catch { /* audit is best-effort */ }
};

// A supplier asked — email every brief owner (falling back to the creator when
// no owners are set). Internal recipients, so no supplier_email_log.
export async function notifyBriefOwnersOfQuestion({ briefId, briefName, supplierName, questionBody }) {
  const { data: owners } = await supabase
    .from('brief_owners')
    .select('internal_users!user_id(full_name, email, is_active)')
    .eq('brief_id', briefId);
  let recipients = (owners || [])
    .map((o) => o.internal_users)
    .filter((u) => u && u.email && u.is_active !== false);
  // Fallback: no owners set → the brief's creator.
  if (recipients.length === 0) {
    const { data: brief } = await supabase
      .from('briefs')
      .select('creator:internal_users!created_by(full_name, email, is_active)')
      .eq('id', briefId).maybeSingle();
    const c = brief?.creator;
    if (c && c.email && c.is_active !== false) recipients = [c];
  }
  let emailed = 0;
  for (const u of recipients) {
    const { subject, html } = briefQuestionAskedEmail({ ownerName: u.full_name, supplierName, briefName, questionBody, appUrl: appUrl() });
    try { await sendEmail({ to: u.email, subject, html }); emailed += 1; } catch { /* best-effort */ }
  }
  return { emailed, owners: recipients.length };
}

// A supplier asked for a post-deadline update — email every brief owner
// immediately (§5), with the supplier's optional message included verbatim.
export async function notifyOwnersOfUpdateRequest({ briefId, briefName, supplierName, message }) {
  const { data: owners } = await supabase
    .from('brief_owners')
    .select('internal_users!user_id(full_name, email, is_active)')
    .eq('brief_id', briefId);
  let recipients = (owners || [])
    .map((o) => o.internal_users)
    .filter((u) => u && u.email && u.is_active !== false);
  if (recipients.length === 0) {
    const { data: brief } = await supabase
      .from('briefs')
      .select('creator:internal_users!created_by(full_name, email, is_active)')
      .eq('id', briefId).maybeSingle();
    const c = brief?.creator;
    if (c && c.email && c.is_active !== false) recipients = [c];
  }
  let emailed = 0;
  for (const u of recipients) {
    const { subject, html } = briefUpdateRequestEmail({ ownerName: u.full_name, supplierName, briefName, message, appUrl: appUrl() });
    try { await sendEmail({ to: u.email, subject, html }); emailed += 1; } catch { /* best-effort */ }
  }
  return { emailed, owners: recipients.length };
}

// A holding status update — email the chosen audience (§4). 'all_waiting' =
// non-declined recipients with no outcome; 'asker' = the one target supplier.
// Declined/withdrawn are never included. decisionByLabel is pre-formatted.
export async function notifyStatusUpdate({ briefId, briefName, decisionByLabel, message, audience, targetSupplierId, sentBy }) {
  let supplierIds = [];
  if (audience === 'asker' && targetSupplierId) {
    supplierIds = [targetSupplierId];
  } else {
    const { data: recips } = await supabase.from('brief_recipients').select('supplier_id, status').eq('brief_id', briefId);
    supplierIds = Array.from(new Set((recips || [])
      .filter((r) => r.supplier_id && r.status !== 'declined' && r.status !== 'withdrawn')
      .map((r) => r.supplier_id)));
  }
  const map = await fetchActivePortalUsersBySupplier(supplierIds);
  let emailed = 0;
  const noAddress = [];
  for (const sid of supplierIds) {
    const users = map.get(sid) || [];
    if (users.length === 0) { noAddress.push(sid); continue; }
    const sentTo = [];
    for (const u of users) {
      const { subject, html } = briefStatusUpdateEmail({ contactName: u.full_name, briefName, decisionBy: decisionByLabel, message, appUrl: appUrl() });
      try { await sendEmail({ to: u.email, subject, html }); sentTo.push(u.email); emailed += 1; } catch { /* best-effort */ }
    }
    await logSupplierEmail(sid, 'brief_status_update', sentTo, sentBy);
  }
  return { emailed, suppliers: supplierIds.length, noAddressCount: noAddress.length };
}

// A supplier's question was answered — email that supplier's active portal users.
export async function notifyAskerAnswered({ supplierId, briefName, sentBy }) {
  const map = await fetchActivePortalUsersBySupplier([supplierId]);
  const users = map.get(supplierId) || [];
  const sentTo = [];
  for (const u of users) {
    const { subject, html } = briefQuestionAnsweredEmail({ contactName: u.full_name, briefName, appUrl: appUrl() });
    try { await sendEmail({ to: u.email, subject, html }); sentTo.push(u.email); } catch { /* best-effort */ }
  }
  await logSupplierEmail(supplierId, 'brief_question_answered', sentTo, sentBy);
  return { emailed: sentTo.length, noAddress: users.length === 0 };
}

// A question was closed unanswered — email the asker's supplier.
export async function notifyAskerClosed({ supplierId, briefName, sentBy }) {
  const map = await fetchActivePortalUsersBySupplier([supplierId]);
  const users = map.get(supplierId) || [];
  const sentTo = [];
  for (const u of users) {
    const { subject, html } = briefQuestionClosedEmail({ contactName: u.full_name, briefName, appUrl: appUrl() });
    try { await sendEmail({ to: u.email, subject, html }); sentTo.push(u.email); } catch { /* best-effort */ }
  }
  await logSupplierEmail(supplierId, 'brief_question_closed', sentTo, sentBy);
  return { emailed: sentTo.length, noAddress: users.length === 0 };
}

// A clarification was published — email every OTHER supplier sent the brief
// (excluding the asker and anyone who declined/withdrew). Anonymous: the email
// never names who asked. Returns suppliers with no active portal user so the
// admin can be warned.
export async function notifyOtherSuppliersPublished({ briefId, briefName, exceptSupplierId, sentBy }) {
  const { data: recips } = await supabase
    .from('brief_recipients')
    .select('supplier_id, status')
    .eq('brief_id', briefId);
  const supplierIds = Array.from(new Set(
    (recips || [])
      .filter((r) => r.supplier_id && r.supplier_id !== exceptSupplierId
        && r.status !== 'declined' && r.status !== 'withdrawn')
      .map((r) => r.supplier_id)
  ));
  const map = await fetchActivePortalUsersBySupplier(supplierIds);
  let emailed = 0;
  const noAddress = [];
  for (const sid of supplierIds) {
    const users = map.get(sid) || [];
    if (users.length === 0) { noAddress.push(sid); continue; }
    const sentTo = [];
    for (const u of users) {
      const { subject, html } = briefQuestionPublishedEmail({ contactName: u.full_name, briefName, appUrl: appUrl() });
      try { await sendEmail({ to: u.email, subject, html }); sentTo.push(u.email); emailed += 1; } catch { /* best-effort */ }
    }
    await logSupplierEmail(sid, 'brief_question_published', sentTo, sentBy);
  }
  return { emailed, suppliers: supplierIds.length, noAddressCount: noAddress.length };
}
